import {Box, Group, SimpleGrid, Stack, Table, Text} from '@mantine/core'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import type {SummaryStatsData} from 'src/api/client/models/analytics'
import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import type {ExportKind} from 'src/components/export-image-button'
import {SectionCard} from 'src/components/section-card'
import {CARTESIAN_GRID_PROPS, CHART_COLORS} from 'src/lib/chart-colors'
import {formatCurrency, formatNumber, formatPercent} from 'src/lib/format'

const ENGAGEMENT_LEVELS = [
    'Very Engaged',
    'Engaged',
    'Somewhat Engaged',
    'Minimally Engaged',
    'Disengaged',
] as const

/** Engagement bands shown on the base-capacity bar chart (excludes Disengaged). */
const CHART_ENGAGEMENT_LEVELS = ENGAGEMENT_LEVELS.filter((e) => e !== 'Disengaged')

const CHART_HEIGHT = 320
const CHART_MARGIN = {top: 24, right: 8, left: 28, bottom: 60}

type Props = {
    data: SummaryStatsData
    onProspectFilter?: (filters: ProspectClickFilters) => void
}

function filtersForEngagement(eng: (typeof ENGAGEMENT_LEVELS)[number]): ProspectClickFilters {
    return {engagementLevel: eng}
}

type ChartRow = {
    engagementLevel: string
    baseCapacity: number
}

function SummaryStatsBaseCapacityChart({
    data,
    onProspectFilter,
}: {
    data: SummaryStatsData
    onProspectFilter?: (filters: ProspectClickFilters) => void
}) {
    const chartRows: ChartRow[] = CHART_ENGAGEMENT_LEVELS.map((eng) => ({
        engagementLevel: eng,
        baseCapacity: Number(data.baseCapacityByEngagement[eng] ?? 0),
    }))
    const chartColumnRef = useRef<HTMLDivElement>(null)
    const [sideBySideChartHeight, setSideBySideChartHeight] = useState(CHART_HEIGHT)

    useLayoutEffect(() => {
        const el = chartColumnRef.current
        if (!el) {
            return
        }
        const updateHeight = () => {
            const h = el.getBoundingClientRect().height
            if (h > 0) {
                setSideBySideChartHeight(Math.floor(h))
            }
        }
        updateHeight()
        const observer = new ResizeObserver(updateHeight)
        observer.observe(el)
        return () => observer.disconnect()
    }, [data.baseCapacityByEngagement])

    const chartPixelHeight = sideBySideChartHeight
    const plotHeight = chartPixelHeight - CHART_MARGIN.top - CHART_MARGIN.bottom

    const renderBarShape = useCallback(
        (props: unknown) => {
            const p = props as {
                x?: number
                y?: number
                width?: number
                height?: number
                fill?: string
                radius?: [number, number, number, number]
                payload?: {engagementLevel?: string}
            }
            const {x = 0, y = 0, width = 0, height = 0, fill, radius, payload} = p
            const rawEng = payload?.engagementLevel
            const filters =
                rawEng != null && ENGAGEMENT_LEVELS.some((e) => e === rawEng)
                    ? filtersForEngagement(rawEng as (typeof ENGAGEMENT_LEVELS)[number])
                    : null

            return (
                <g>
                    <Rectangle
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={fill ?? CHART_COLORS.secondary}
                        radius={radius ?? [4, 4, 0, 0]}
                        style={onProspectFilter && filters ? {cursor: 'pointer'} : undefined}
                    />
                    {onProspectFilter && filters && (
                        <rect
                            x={x}
                            y={0}
                            width={width}
                            height={plotHeight}
                            fill="transparent"
                            style={{cursor: 'pointer'}}
                            onClick={() => onProspectFilter(filters)}
                        />
                    )}
                </g>
            )
        },
        [onProspectFilter, plotHeight],
    )

    const chart = (
        <ResponsiveContainer width="100%" height={chartPixelHeight}>
            <BarChart data={chartRows} margin={CHART_MARGIN}>
                <CartesianGrid {...CARTESIAN_GRID_PROPS} />
                <XAxis
                    dataKey="engagementLevel"
                    angle={-35}
                    textAnchor="end"
                    height={80}
                    interval={0}
                />
                <YAxis
                    type="number"
                    allowDecimals={false}
                    tickFormatter={(v: number) => formatCurrency(v)}
                    width={70}
                />
                <Tooltip
                    formatter={(value: number) => [formatCurrency(value), 'Base Capacity']}
                    labelFormatter={(label) => label}
                />
                <Bar
                    dataKey="baseCapacity"
                    fill={CHART_COLORS.secondary}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                    shape={renderBarShape}
                >
                    <LabelList
                        dataKey="baseCapacity"
                        position="top"
                        formatter={(v: number) => formatCurrency(v)}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )

    return (
        <Box
            ref={chartColumnRef}
            style={{
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box style={{flex: 1, minHeight: 0, width: '100%'}}>{chart}</Box>
        </Box>
    )
}

function SummaryStatsConversionTable({
    data,
    onProspectFilter,
    maw,
}: {
    data: SummaryStatsData
    onProspectFilter?: (filters: ProspectClickFilters) => void
    maw?: number | string
}) {
    const prospectsByEngagement = ENGAGEMENT_LEVELS.reduce(
        (acc, eng) => {
            acc[eng] = data.crossTab.rows.reduce((sum, row) => sum + (row[eng] || 0), 0)
            return acc
        },
        {} as Record<(typeof ENGAGEMENT_LEVELS)[number], number>,
    )

    return (
        <Box maw={maw} w="100%" miw={0}>
            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Engagement Level</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Prospects</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Conversion Rate</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Base Capacity</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {ENGAGEMENT_LEVELS.map((eng) => {
                        const prospects = prospectsByEngagement[eng] ?? 0
                        const rate = data.rates[eng] ?? 0
                        const baseCap = data.baseCapacityByEngagement[eng] ?? '0'
                        const handleClick = onProspectFilter
                            ? () => onProspectFilter({engagementLevel: eng})
                            : undefined
                        return (
                            <Table.Tr key={eng}>
                                <Table.Td
                                    onClick={handleClick}
                                    style={handleClick ? {cursor: 'pointer'} : undefined}
                                >
                                    {eng}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatNumber(prospects)}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatPercent(rate)}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatCurrency(baseCap)}
                                </Table.Td>
                            </Table.Tr>
                        )
                    })}
                </Table.Tbody>
            </Table>
        </Box>
    )
}

function SummaryStatsHeaderStats({data}: {data: SummaryStatsData}) {
    return (
        <Group gap="xl" wrap="nowrap">
            <div>
                <Text c="dimmed">Total Prospects</Text>
                <Text fw={600}>{formatNumber(data.totalProspects)}</Text>
            </div>
            <div>
                <Text c="dimmed">Aggregate Base Capacity</Text>
                <Text fw={600}>{formatCurrency(data.aggregateBaseCapacity)}</Text>
            </div>
        </Group>
    )
}

function SummaryStatsBody({
    data,
    onProspectFilter,
    includeChart,
}: {
    data: SummaryStatsData
    onProspectFilter?: (filters: ProspectClickFilters) => void
    includeChart: boolean
}) {
    return (
        <Stack gap="md">
            <SummaryStatsHeaderStats data={data} />
            {includeChart ? (
                <SimpleGrid cols={{base: 1, lg: 2}} spacing="lg">
                    <SummaryStatsBaseCapacityChart
                        data={data}
                        onProspectFilter={onProspectFilter}
                    />
                    <SummaryStatsConversionTable
                        data={data}
                        onProspectFilter={onProspectFilter}
                        maw="100%"
                    />
                </SimpleGrid>
            ) : (
                <SummaryStatsConversionTable data={data} onProspectFilter={onProspectFilter} />
            )}
        </Stack>
    )
}

export function SummaryStatsExportContent({
    data,
    kind,
}: {
    data: SummaryStatsData
    kind: ExportKind
}) {
    if (kind === 'chart') {
        return <SummaryStatsBaseCapacityChart data={data} />
    }
    return (
        <Stack gap="md">
            <SummaryStatsHeaderStats data={data} />
            <SummaryStatsConversionTable data={data} />
        </Stack>
    )
}

export default function SummaryStats({data, onProspectFilter}: Props) {
    return (
        <SectionCard title="Conversion by Engagement Level">
            <SummaryStatsBody data={data} onProspectFilter={onProspectFilter} includeChart />
        </SectionCard>
    )
}
